import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../lib/config';

interface Video {
  id: number;
  title: string;
  description: string;
  file_path: string; // This is the Bunny.net URL
  thumbnail_url?: string;
  upload_date: string;
  duration_seconds?: number;
  file_size?: number;
}

const Events: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch videos from the API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(getApiUrl('api/videos'));
        if (!response.ok) {
          throw new Error('Failed to fetch videos');
        }
        const data = await response.json();
        // The API returns { videos: [...], total, page, limit, pages }
        setVideos(Array.isArray(data.videos) ? data.videos : []);
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to load videos');
        setVideos([]); // Ensure videos is always an array
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Format file size for display
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Format duration for display
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown duration';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get Bunny.net video player embed URL
  const getBunnyPlayerUrl = (videoUrl: string): string => {
    // Check if this is already a streaming URL or a storage URL
    if (videoUrl.includes('iframe.mediadelivery.net')) {
      // Already an embed URL, return as is
      return videoUrl;
    } else if (videoUrl.includes('b-cdn.net')) {
      // This is a storage URL, extract filename and create embed URL
      const filename = videoUrl.split('/').pop() || '';
      const videoId = filename.split('.')[0];
      return `https://iframe.mediadelivery.net/embed/288500/${videoId}?autoplay=false&loop=false&muted=false&preload=true&responsive=true`;
    } else {
      // Fallback: use direct video URL
      return videoUrl;
    }
  };

  if (loading) {
    return (
      <div className="events-container">
        <div className="events-header">
          <h1>🎬 Radio Station Events</h1>
          <p>Loading videos...</p>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container">
        <div className="events-header">
          <h1>🎬 Radio Station Events</h1>
          <p className="error-message">❌ {error}</p>
        </div>
      </div>
    );
  }

  // YouTube-like layout with video player and playlist

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      fontFamily: 'Tahoma, sans-serif',
      backgroundColor: '#f0f0f0'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(180deg, #0078d4 0%, #106ebe 100%)',
        color: 'white',
        padding: '10px 15px',
        borderBottom: '2px solid #003c71',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ margin: 0, fontSize: '16px' }}>🎬 Radio Station Events</h2>
        <p style={{ margin: '2px 0 0 0', fontSize: '11px', opacity: 0.9 }}>
          Browse and watch our latest video content
        </p>
      </div>

      {videos.length === 0 ? (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📭</div>
          <h3 style={{ fontSize: '16px', margin: 0 }}>No videos available</h3>
          <p style={{ fontSize: '12px', margin: '5px 0 0 0' }}>Check back later for new content!</p>
        </div>
      ) : (
        <div style={{ 
          flex: 1, 
          display: 'flex',
          overflow: 'hidden'
        }}>
          {/* Video Player Section (Left) */}
          <div style={{ 
            flex: '2',
            padding: '15px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {selectedVideo ? (
              <>
                {/* Video Player */}
                <div style={{
                  width: '100%',
                  height: '300px',
                  backgroundColor: '#000',
                  border: '2px inset #c0c0c0',
                  borderRadius: '5px',
                  marginBottom: '15px',
                  position: 'relative'
                }}>
                  {selectedVideo.file_path.includes('iframe.mediadelivery.net') ? (
                    <iframe
                      src={getBunnyPlayerUrl(selectedVideo.file_path)}
                      style={{
                        border: 'none',
                        width: '100%',
                        height: '100%',
                        borderRadius: '3px'
                      }}
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      controls
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#000',
                        borderRadius: '3px'
                      }}
                      poster={selectedVideo.thumbnail_url}
                    >
                      <source src={selectedVideo.file_path} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>

                {/* Video Details */}
                <div style={{
                  backgroundColor: 'white',
                  border: '2px inset #c0c0c0',
                  borderRadius: '5px',
                  padding: '15px'
                }}>
                  <h3 style={{ 
                    margin: '0 0 10px 0', 
                    fontSize: '14px', 
                    color: '#0078d4',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '5px' 
                  }}>
                    {selectedVideo.title}
                  </h3>
                  <p style={{ 
                    fontSize: '11px', 
                    color: '#666', 
                    margin: '0 0 10px 0',
                    lineHeight: '1.4'
                  }}>
                    {selectedVideo.description}
                  </p>
                  <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    fontSize: '10px', 
                    color: '#888' 
                  }}>
                    <span>⏱️ {formatDuration(selectedVideo.duration_seconds)}</span>
                    <span>💾 {formatFileSize(selectedVideo.file_size)}</span>
                    <span>📅 {new Date(selectedVideo.upload_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#666',
                backgroundColor: 'white',
                border: '2px inset #c0c0c0',
                borderRadius: '5px'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎬</div>
                <h3 style={{ fontSize: '16px', margin: 0 }}>Select a video to play</h3>
                <p style={{ fontSize: '12px', margin: '5px 0 0 0' }}>Choose from the playlist on the right</p>
              </div>
            )}
          </div>

          {/* Video List Section (Right) */}
          <div style={{ 
            flex: '1',
            borderLeft: '2px solid #c0c0c0',
            padding: '15px',
            backgroundColor: '#f8f8f8',
            overflow: 'auto'
          }}>
            <h4 style={{ 
              margin: '0 0 15px 0', 
              fontSize: '12px', 
              color: '#0078d4',
              borderBottom: '1px solid #c0c0c0',
              paddingBottom: '5px'
            }}>
              📋 Video Playlist ({videos.length})
            </h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {videos.map((video) => (
                <div 
                  key={video.id} 
                  onClick={() => setSelectedVideo(video)}
                  style={{
                    display: 'flex',
                    backgroundColor: selectedVideo?.id === video.id ? '#e6f3ff' : 'white',
                    border: selectedVideo?.id === video.id ? '2px solid #0078d4' : '1px solid #c0c0c0',
                    borderRadius: '3px',
                    padding: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (selectedVideo?.id !== video.id) {
                      e.currentTarget.style.backgroundColor = '#f0f8ff';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedVideo?.id !== video.id) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: '60px',
                    height: '45px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '10px',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {video.thumbnail_url ? (
                      <img 
                        src={video.thumbnail_url} 
                        alt={video.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '16px', color: '#999' }}>🎬</span>
                    )}
                    {selectedVideo?.id === video.id && (
                      <div style={{
                        position: 'absolute',
                        backgroundColor: 'rgba(0, 120, 212, 0.8)',
                        color: 'white',
                        fontSize: '12px',
                        padding: '2px 4px',
                        borderRadius: '2px'
                      }}>
                        ▶️
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h5 style={{ 
                      margin: '0 0 4px 0', 
                      fontSize: '11px', 
                      fontWeight: 'bold',
                      color: '#333',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {video.title}
                    </h5>
                    <p style={{ 
                      margin: '0 0 4px 0', 
                      fontSize: '9px', 
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {video.description}
                    </p>
                    <div style={{ 
                      fontSize: '8px', 
                      color: '#888',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{formatDuration(video.duration_seconds)}</span>
                      <span>{new Date(video.upload_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events; 